/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';
import CarStatusBadge from './car-status-badge.component';

import Typography from '@/components/shared/typography/typography.component';

import Link from '@/components/WrappedLink';
import { useAnalytics } from '@/integrations';
import { ChevronRight } from '@mui/icons-material';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { HighlightsIcon, LocationMarkerPin } from 'public/Images';
import myImageLoader from '@/image.local.loader';
import { AVAILABLE_LANGUAGES, colors } from '@/constants';
import { numberWithCommas, formatNumber, slugify, isValidUUid } from '@/utils';
import React, { memo } from 'react';
import { CarListingData } from '@/types/cars.types';
import CarDetailsSectionSeparator from '@/components/car-details/CarProfileNew/CarProfileInfo/CarDetailsSectionSeparator';
import useTranslationHook from '@/hook/use-translation-client.hook';
import { Trans } from 'react-i18next';
import { CarTypesEnum, getCarType } from '@/components/car-details-v2/car-type';
import CarIncludeBadge from './car-include-badge.component';
import { FEATURE_FLAG_SHOW_LISTING_LOCATION } from '@/constants';
import CarListingShadow, {
    getShadowColorByCarType,
} from './car-listing-shadow.component';
import {
    AvailableCarIncludes,
    CarIncludesMap,
} from '@/components/car-details-v2/CarProfileSections/CarIncludes/car-includes.type';
import { CarTypeBadge } from '@/components/car-details-v2/CarProfileSections/CarTypeBadge/CarTypeBadge';

const CARD_SHADOW_STYLE = {
    boxShadow:
        '0px 1px 3px rgba(16,24,40,0.1), 0px 1px 2px rgba(16,24,40,0.06)',
    borderRadius: '0.75rem',
} as const;

const CarDisplayCardClientV2 = memo(
    ({
        carData: {
            id,
            make,
            model,
            year,
            sfVehicleName: carName,
            installmentPrice,
            sylndrSellingPrice: totalPrice,
            kilometers,
            transmission,
            trim,
            isReserved,
            isPurchaseInProgress,
            discountPrice,
            isNewlyAdded,
            showroom,
            category,
            carCards,
            highlights,
        },
        sourceScreen,
        appliedFilter,
        appliedSort,
        position,
        useNextImage = false,
    }: {
        carData: CarListingData;
        sourceScreen: string;
        appliedFilter: boolean;
        appliedSort: boolean;
        position: number;
        useNextImage?: boolean;
    }) => {
        const router = useRouter();
        const { t, IS_RTL, i18n } = useTranslationHook();
        const lang = i18n.language as AVAILABLE_LANGUAGES;
        const cardTitle = `${t(make) + ' ' + t(model) + ' ' + year}`;
        const { trackEvent } = useAnalytics();
        const carType = getCarType({ category });
        const requiredIncludes = CarIncludesMap[carType];
        const toBeIcluded = requiredIncludes.map(i => AvailableCarIncludes[i]);
        const listingShadowColor = getShadowColorByCarType(carType);
        const hasHighlights =
            highlights?.ar?.length > 0 && highlights?.en?.length > 0;
        const highlightsValue = IS_RTL ? highlights?.ar : highlights?.en;

        const goToCarPage = () => {
            trackEvent('Action: Click on Car Card', {
                carName,
                make,
                model,
                year,
                trim,
                kilometers,
                price: totalPrice,
                monthlyInstallment: installmentPrice,
                status: isPurchaseInProgress
                    ? 'Being Sold'
                    : isReserved
                    ? 'Reserved'
                    : 'Not Reserved',
                transmission,
                sourceScreen,
                appliedFilter,
                appliedSort,
                position,
                newlyAdded:
                    isNewlyAdded && !(isReserved || isPurchaseInProgress),
                carLink: `${window.location.origin}/${lang}/car-details/${id}`,
                category,
            });
            if (isValidUUid(id)) {
                router.push(`/en/car-details/${id}`);
            }
        };

        const scrollToSimilarCars = () => {
            if (isValidUUid(id)) {
                const targetUrl = `/${lang}/car-details/${slugify(
                    make
                )}/${slugify(model)}/${id}#similar-cars`;
                router.push(targetUrl);
            }
        };

        const isDealerCar = carType === CarTypesEnum.Dealer;

        const showroomLocalization = showroom?.address?.[lang];
        const showroomDealer = showroom?.instructions?.[lang];
        const address = isDealerCar ? showroomDealer : showroomLocalization;

        const hasDiscount = discountPrice !== undefined && discountPrice !== 0;
        const canPurchase = !isPurchaseInProgress;
        const enableListingLocation = FEATURE_FLAG_SHOW_LISTING_LOCATION;

        return (
            isValidUUid(id) && (
                <Link
                    prefetch={false}
                    href={`/${lang}/car-details/${slugify(make)}/${slugify(
                        model
                    )}/${id}`}
                    style={{
                        ...CARD_SHADOW_STYLE,
                        boxShadow: '0 2px 14px 0 rgba(0,0,0,0.08)',
                    }}
                    className={'flex flex-col relative w-full h-full bg-white'}
                    onClick={() => goToCarPage()}
                >
                    <div className="z-20 absolute top-0 end-0">
                        <CarTypeBadge
                            carType={carType}
                            className="ar:rounded-tl-xl ar:rounded-br-xl en:rounded-tr-xl en:rounded-bl-xl !px-2"
                        />
                    </div>
                    <div className="z-20">
                        <CarStatusBadge
                            isPurchaseInProgress={isPurchaseInProgress}
                            isReserved={isReserved}
                            isNewlyAdded={isNewlyAdded}
                        />
                    </div>
                    <div className={'relative h-20 w-full'}>
                        <div className="absolute left-0  right-0 z-[2] overflow-hidden rounded-t-xl">
                            <CarListingShadow color={listingShadowColor} />
                        </div>
                        <div className="absolute w-full h-22 bottom-0 bg-contain z-10">
                            {carCards?.length > 0 ? (
                                useNextImage ? (
                                    <Image
                                        className="w-[220px] h-[136px] max-h-[120px] object-cover mx-auto px-3"
                                        width={220}
                                        height={136}
                                        quality={100}
                                        src={carCards[0]}
                                        alt={cardTitle}
                                        placeholder={'empty'}
                                        loading={'eager'}
                                        priority={true}
                                        fetchPriority={'high'}
                                    />
                                ) : (
                                    <img
                                        className="w-[220px] h-[136px] max-h-[120px] object-cover mx-auto px-3"
                                        width={220}
                                        height={136}
                                        src={carCards[0] ?? ''}
                                        alt={cardTitle}
                                        loading={'eager'}
                                        fetchPriority={'high'}
                                    />
                                )
                            ) : null}
                        </div>
                    </div>

                    {/* Card Main body */}
                    <section className="flex flex-col px-3 z-10">
                        <div className="flex gap-1 items-end min-h-16 min-w-full">
                            <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                                <Typography
                                    style={{
                                        color: colors['gray-800'],
                                    }}
                                    variant={'body2Medium'}
                                    component="h2"
                                    className="truncate"
                                >
                                    {cardTitle}
                                </Typography>
                                <div className="flex items-center gap-0.5 text-gray-500 overflow-hidden">
                                    <Typography
                                        variant={'caption'}
                                        className="shrink-0 truncate"
                                    >
                                        {IS_RTL ? trim?.ar : trim?.en}
                                    </Typography>
                                    <CarDetailsSectionSeparator />
                                    <Typography
                                        variant="caption"
                                        className="truncate"
                                    >
                                        {t('kilometersWithValue', {
                                            value: formatNumber({
                                                value: +kilometers,
                                                isDiscount: false,
                                            }),
                                        })}
                                    </Typography>
                                </div>
                            </div>

                            {canPurchase ? (
                                // Pricing & Installment section
                                <div className="flex flex-col justify-center items-end gap-0.5 min-w-fit shrink-0">
                                    <div className="flex items-baseline gap-1.5">
                                        {hasDiscount && (
                                            <Typography
                                                className={
                                                    'text-error-600 line-through text-caption font-poppins'
                                                }
                                            >
                                                {numberWithCommas(totalPrice)}
                                            </Typography>
                                        )}
                                        <div className="flex items-center justify-center pt-0.5 px-1 rounded-full bg-secondary-25">
                                            <Typography
                                                className="text-gray-900"
                                                variant="caption2"
                                            >
                                                {t('priceIsFinal')}
                                            </Typography>
                                        </div>
                                    </div>
                                    <div className="flex flex-nowrap items-baseline">
                                        <Trans
                                            i18nKey="carPriceValue"
                                            values={{
                                                value: numberWithCommas(
                                                    hasDiscount
                                                        ? discountPrice
                                                        : totalPrice
                                                ),
                                            }}
                                            components={{
                                                currency: (
                                                    <Typography
                                                        color={
                                                            colors['gray-800']
                                                        }
                                                        variant="caption2"
                                                    />
                                                ),
                                                value: (
                                                    <Typography className="text-body1SemiBold text-gray-800 font-poppins mx-0.5" />
                                                ),
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-nowrap items-baseline">
                                        <Trans
                                            i18nKey="installmentMonthly"
                                            values={{
                                                value: numberWithCommas(
                                                    installmentPrice
                                                ),
                                            }}
                                            components={{
                                                value: (
                                                    <Typography className="text-body2Medium text-gray-800 font-poppins" />
                                                ),
                                                slash: (
                                                    <Typography
                                                        color={
                                                            colors['gray-800']
                                                        }
                                                        variant="caption"
                                                        className="font-poppins"
                                                    />
                                                ),
                                                month: (
                                                    <div className="text-caption2Medium text-gray-800 mx-0.5" />
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                // Similar cars Section
                                <div>
                                    <button
                                        className="bg-white py-[7px] ms-1 ps-4 pe-3 rounded-lg cursor-pointer"
                                        style={{
                                            border: '1px solid #d0d5dd',
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            scrollToSimilarCars();
                                        }}
                                    >
                                        <div className="flex gap-1.5 items-center">
                                            <Typography
                                                variant="captionMedium"
                                                className="text-gray-700 ar:mt-0.5"
                                            >
                                                {t('Similar Cars')}
                                            </Typography>
                                            <ChevronRight
                                                className={
                                                    'ar:rotate-180 w-4 h-4 text-black'
                                                }
                                            />
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Car Includes Section */}
                    <section className="py-2 px-3 bg-white">
                        <div className="flex items-center overflow-x-hidden scrollbar-hide gap-0">
                            {toBeIcluded.map((item, idx) => (
                                <div
                                    key={item.title.en}
                                    className="relative flex items-center shrink-0"
                                >
                                    <CarIncludeBadge
                                        text={
                                            lang === 'ar'
                                                ? item.title.ar
                                                : item.title.en
                                        }
                                    />
                                    {idx < toBeIcluded.length - 1 && (
                                        <Typography className="text-gray-600 mx-1.5">
                                            •
                                        </Typography>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Location Section */}
                    {enableListingLocation && showroom && (
                        <section className="flex gap-1 items-center px-3 pb-2 pt-1 mb-auto">
                            <img
                                width={12}
                                height={12}
                                src={myImageLoader({
                                    src: LocationMarkerPin.src,
                                    width: 12 * 4,
                                    height: 12 * 4,
                                    quality: 90,
                                })}
                                alt={'location-pin'}
                                loading={'lazy'}
                                fetchPriority={'low'}
                            />
                            <Typography
                                variant="caption2"
                                className="text-gray-500 ar:mt-0.5 truncate"
                            >
                                {address}
                            </Typography>
                        </section>
                    )}
                    {/* Highlights Section */}
                    {hasHighlights && (
                        <section
                            className="flex px-3 py-2 items-center overflow-hidden"
                            style={{
                                background:
                                    'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.04) 100%)',
                            }}
                        >
                            {highlightsValue.map((highlight, idx) => (
                                <React.Fragment key={idx}>
                                    {idx > 0 && (
                                        <Typography className="text-caption text-gray-500 mx-1">
                                            {'•'}
                                        </Typography>
                                    )}
                                    <div className="flex flex-row items-center gap-2 shrink-0">
                                        <Image
                                            alt="highlights"
                                            src={HighlightsIcon.src}
                                            width={14}
                                            height={14}
                                        />

                                        <Typography className="text-caption2Medium text-gray-800 whitespace-nowrap">
                                            {highlight}
                                        </Typography>
                                    </div>
                                </React.Fragment>
                            ))}
                        </section>
                    )}
                </Link>
            )
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.carData.id === nextProps.carData.id &&
            prevProps.carData.isNewlyAdded === nextProps.carData.isNewlyAdded &&
            prevProps.carData.isPurchaseInProgress ===
                nextProps.carData.isPurchaseInProgress &&
            prevProps.carData.isReserved === nextProps.carData.isReserved &&
            prevProps.carData.discountPrice ===
                nextProps.carData.discountPrice &&
            prevProps.carData.sylndrSellingPrice ===
                nextProps.carData.sylndrSellingPrice &&
            prevProps.appliedFilter === nextProps.appliedFilter &&
            prevProps.appliedSort === nextProps.appliedSort &&
            prevProps.position === nextProps.position
        );
    }
);
CarDisplayCardClientV2.displayName = 'CarDisplayCardClient';
export default CarDisplayCardClientV2;
